import React, { useState, useEffect } from 'react';

import cn from 'classnames';
import { isNil, noop } from 'lodash';
import {
  func,
  number,
  string,
  bool,
  shape,
  oneOf,
  oneOfType,
  node,
} from 'prop-types';

import { faLoader } from '@fortawesome/pro-solid-svg-icons/faLoader';

import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';

import PasswordInputAdornment from 'components/Common/TextInput/components/PasswordInputAdornment';

import v from 'components/variables';

import s from './TextInput.css';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiFormLabel-asterisk': {
      color: v.redDark,
    },
  },
}));

const TextInput = React.forwardRef(
  (
    {
      id,
      name,
      error,
      rows,
      label,
      value,
      onBlur,
      variant,
      onFocus,
      classes,
      onChange,
      disabled,
      fullWidth,
      rootClass,
      charLimit,
      autoFocus,
      multiline,
      helperText,
      onKeyPress,
      InputProps,
      inputProps,
      placeholder,
      defaultValue,
      outlinedInputClasses,
      enableHardLimit, // prevent user to input more characters than charLimit
      compactCharLimit, // transform charLimit styles so that it does not affect the layout
      hasRedAsterisk,
      InputLabelProps,
      testId,
      loading,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) => {
    const [charCount, setCharCount] = useState(value?.length || 0);
    const [showPassword, setShowPassword] = useState(false);
    const [hasCharLimitError, setHasCharLimitError] = useState(false);
    const inputClasses = useStyles();

    const inputValue = enableHardLimit ? value.slice(0, charLimit) : value;

    const hasStartAdornment = loading || Boolean(InputProps?.startAdornment);

    useEffect(() => {
      const onCharLimit = newValue => {
        if (!charLimit || isNil(newValue)) {
          return;
        }

        let noOfChars = newValue.length;
        if (enableHardLimit && noOfChars > charLimit) {
          noOfChars = charLimit;
        }
        setCharCount(noOfChars);
        setHasCharLimitError(noOfChars > charLimit);
      };

      onCharLimit(value);
    }, [enableHardLimit, charLimit, value]);

    const onInputChange = event => {
      onChange(event);
    };

    const inputTypePassword = InputProps.type === 'password';

    const renderStartAdornment = () =>
      loading ? (
        <FontAwesomeIcon spin icon={faLoader} color="gray" />
      ) : (
        InputProps?.startAdornment
      );

    const renderEndAdornment = () =>
      inputTypePassword ? (
        <>
          <PasswordInputAdornment
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />
          {InputProps?.endAdornment}
        </>
      ) : (
        InputProps?.endAdornment
      );

    // This is for to prevent console error about component shouldn't be controlled by value and defaultValue at the same time.
    // This will make it get error on console only when we use both at the same time, not always when we use TextInput component
    const getDefaultValueProps = () => {
      if (defaultValue) {
        return { defaultValue };
      }
      return {};
    };

    // Combine inputProps with maxLength when enableHardLimit is true
    const combinedInputProps = {
      ...inputProps,
      'aria-label': ariaLabel,
      'data-testid': testId,
      ...(enableHardLimit && charLimit ? { maxLength: charLimit } : {}),
    };

    return (
      <TextField
        {...getDefaultValueProps()}
        ref={ref}
        id={id}
        rows={rows}
        name={name}
        label={label}
        value={inputValue}
        onBlur={onBlur}
        onFocus={onFocus}
        variant={variant}
        multiline={multiline}
        onKeyPress={onKeyPress}
        onChange={onInputChange}
        disabled={loading || disabled}
        error={error || hasCharLimitError}
        placeholder={loading ? 'loading' : placeholder}
        classes={{
          root: cn(s.root, rootClass, {
            [s.rootWithStartAdornment]: hasStartAdornment,
            [s.fullWidth]: fullWidth,
            [inputClasses.root]: hasRedAsterisk,
            [s.rootLoading]: loading,
          }),
          ...classes,
        }}
        FormHelperTextProps={{
          classes: {
            root: cn(s.helperTextBox, {
              [s.compactCharLimit]: compactCharLimit,
            }),
          },
        }}
        InputLabelProps={{
          shrink: true,
          classes: { root: s.label },
          ...InputLabelProps,
        }}
        InputProps={{
          ...InputProps,
          type: showPassword ? 'text' : InputProps?.type,
          startAdornment: renderStartAdornment(),
          endAdornment: renderEndAdornment(),
          classes: {
            ...outlinedInputClasses,
            root: cn(s.outlinedInputRoot, outlinedInputClasses.root),
          },
        }}
        autoFocus={autoFocus}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={combinedInputProps}
        helperText={
          helperText || charLimit ? (
            <>
              <Typography variant="caption">{helperText}</Typography>
              {charLimit ? (
                <Typography variant="caption">
                  {charCount}/{charLimit}
                </Typography>
              ) : null}
            </>
          ) : null
        }
        {...rest}
      />
    );
  },
);

TextInput.propTypes = {
  onBlur: func,
  rows: number,
  onFocus: func,
  error: oneOfType([string, bool]),
  label: oneOfType([string, node]),
  disabled: bool,
  fullWidth: bool,
  multiline: bool,
  onKeyPress: func,
  charLimit: number,
  classes: shape({}),
  helperText: string,
  placeholder: string,
  rootClass: shape({}),
  id: string,
  inputProps: shape({}),
  name: string.isRequired,
  onChange: func.isRequired,
  outlinedInputClasses: shape({}),
  variant: oneOf(['filled', 'outlined', 'standard']),
  defaultValue: oneOfType([string, number]),
  value: oneOfType([string, number]).isRequired,
  autoFocus: bool,
  InputProps: shape({
    type: string,
    startAdornment: node,
    endAdornment: node,
  }),
  enableHardLimit: bool,
  compactCharLimit: bool,
  hasRedAsterisk: bool,
  InputLabelProps: shape({}),
  testId: string,
  loading: bool,
  'aria-label': string,
};

TextInput.defaultProps = {
  label: '',
  id: undefined,
  rows: 5,
  classes: {},
  charLimit: 0,
  onBlur: noop,
  error: false,
  onFocus: noop,
  rootClass: {},
  helperText: '',
  inputProps: {},
  InputProps: {},
  disabled: false,
  placeholder: '',
  defaultValue: '',
  onKeyPress: noop,
  multiline: false,
  autoFocus: false,
  fullWidth: false,
  variant: 'outlined',
  outlinedInputClasses: {},
  enableHardLimit: false,
  compactCharLimit: false,
  hasRedAsterisk: false,
  InputLabelProps: {},
  testId: '',
  loading: false,
  'aria-label': '',
};

export default TextInput;
